import React, { memo, useMemo } from 'react';

import { Box } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  AiProcessAwaitingStatuses,
  AiProcessExecutionStatus,
  AiProcessStepExecutionStatus,
} from 'constants/aiProcessRuns';

import { containerStyles, getBarStyles } from './StepMiniBars.styles';

const DEFAULT_BAR_COUNT = 5;

const stateForStep = ({ stepNumber, completedSteps, status }) => {
  if (stepNumber <= completedSteps) {
    return 'done';
  }

  const isNextStep = stepNumber === completedSteps + 1;

  if (
    isNextStep &&
    (status === AiProcessExecutionStatus.RUNNING || status === AiProcessExecutionStatus.PENDING)
  ) {
    return 'working';
  }

  if (isNextStep && status === AiProcessExecutionStatus.FAILED) {
    return 'failed';
  }

  if (status === AiProcessExecutionStatus.CANCELLED) {
    return 'cancelled';
  }

  return 'pending';
};

const STEP_STATUS_TO_BAR_STATE = {
  [AiProcessStepExecutionStatus.COMPLETED]: 'done',
  [AiProcessStepExecutionStatus.RUNNING]: 'working',
  [AiProcessStepExecutionStatus.FAILED]: 'failed',
  [AiProcessStepExecutionStatus.SKIPPED]: 'cancelled',
  [AiProcessStepExecutionStatus.CANCELLED]: 'cancelled',
};

const stateForStepStatus = stepStatus =>
  AiProcessAwaitingStatuses.has(stepStatus)
    ? 'attention'
    : (STEP_STATUS_TO_BAR_STATE[stepStatus] ?? 'pending');

const computeBarStates = ({ totalSteps, completedSteps, status, barCount }) => {
  if (totalSteps === 0) {
    return Array.from({ length: barCount }, (_unused, index) => ({
      key: `bar-position-${index}`,
      state: 'pending',
    }));
  }

  const ratio = barCount / totalSteps;

  return Array.from({ length: barCount }, (_unused, index) => ({
    key: `bar-position-${index}`,
    state: stateForStep({
      stepNumber: Math.ceil((index + 1) / ratio),
      completedSteps,
      status,
    }),
  }));
};

const StepMiniBars = ({
  totalSteps,
  completedSteps,
  status,
  stepStatuses,
  barCount = DEFAULT_BAR_COUNT,
}) => {
  const states = useMemo(
    () =>
      !_.isEmpty(stepStatuses)
        ? stepStatuses.map((stepStatus, index) => ({
            key: `bar-position-${index}`,
            state: stateForStepStatus(stepStatus),
          }))
        : computeBarStates({
            totalSteps: _.isFinite(totalSteps) ? totalSteps : 0,
            completedSteps: _.isFinite(completedSteps) ? completedSteps : 0,
            status,
            barCount,
          }),
    [totalSteps, completedSteps, status, stepStatuses, barCount],
  );

  return (
    <Box sx={containerStyles}>
      {states.map(({ key, state }) => (
        <Box key={key} sx={getBarStyles(state)} />
      ))}
    </Box>
  );
};

StepMiniBars.propTypes = {
  totalSteps: PropTypes.number,
  completedSteps: PropTypes.number,
  status: PropTypes.string,
  barCount: PropTypes.number,
  stepStatuses: PropTypes.arrayOf(PropTypes.string),
};

export default memo(StepMiniBars);
